'use client';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@banzai/ui/components/form';
import { Input } from '@banzai/ui/components/input';
import { Textarea } from '@banzai/ui/components/textarea';
import { Control, FieldPath, FieldValues } from 'react-hook-form';
import { RequiredMark } from '@banzai/ui/components/required-mark';

interface FormFieldWrapperProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  description?: string;
  required?: boolean;
  type?: 'input' | 'textarea';
  placeholder?: string;
  inputType?: 'text' | 'number' | 'email' | 'password' | 'url' | 'tel' | 'search';
  min?: number;
  max?: number;
  customOnChange?: (value: string, onChange: (value: any) => void) => void;
  forceTextarea?: boolean;
  className?: string;
}

export function FormFieldWrapper<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  required = false,
  type = 'input',
  placeholder,
  inputType = 'text',
  min,
  max,
  customOnChange,
  forceTextarea,
  className,
}: FormFieldWrapperProps<TFieldValues, TName>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>
            {label} {required && <RequiredMark />}
          </FormLabel>
          <FormControl>
            {forceTextarea || type === 'textarea' ? (
              <Textarea {...field} placeholder={placeholder} className="min-h-32" />
            ) : (
              <Input
                {...field}
                type={inputType}
                placeholder={placeholder}
                min={min}
                max={max}
                onChange={
                  customOnChange
                    ? e => customOnChange(e.target.value, field.onChange)
                    : field.onChange
                }
              />
            )}
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
